import React, { useMemo } from 'react'
import styled from 'styled-components'
import * as echarts from 'echarts'
import ReactECharts from 'echarts-for-react'
import { Box, Flex, Icon, Loading } from '../../atoms'
import {
  ParticipantDemographicsChartProps,
  DEFAULT_PARTICIPANT_DEMOGRAPHICS_CHART_LABELS,
} from './ParticipantDemographicsChart.types'
import {
  formatPercentage,
  formatNumber,
} from './ParticipantDemographicsChart.utils'

const DashboardContainer = styled.div`
  width: 100%;
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`

const MetricCard = styled(Box)`
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  text-align: center;
`

const MetricValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 8px 0;
`

const MetricLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 4px;
`

const ChartsGrid = styled.div`
  display: grid;
  gap: 24px;
  margin-bottom: 32px;
`

const ChartContainer = styled(Box)`
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 20px;
`

const GenderBar = styled.div<{ $gender: 'male' | 'female' }>`
  background: ${({ $gender }) => ($gender === 'male' ? '#3b82f6' : '#ec4899')};
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 500;
  font-size: 14px;
`

const GenderLegend = styled(Flex)`
  gap: 24px;
  margin-top: 12px;
  justify-content: center;
`

const LegendItem = styled(Flex)`
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const LegendDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`

const SmallChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
`

export const ParticipantDemographicsChart: React.FC<
  ParticipantDemographicsChartProps
> = ({ data, loading = false, error = null, labels = {} }) => {
  const mergedLabels = {
    ...DEFAULT_PARTICIPANT_DEMOGRAPHICS_CHART_LABELS,
    ...labels,
  }
  const genderOverTimeOption = useMemo(() => {
    if (!data || !data.genderOverTime || data.genderOverTime.length === 0) {
      return null
    }

    // Calculate percentages for each year
    const percentageData = data.genderOverTime.map((item) => {
      const total = item.male + item.female
      return {
        year: item.year,
        femalePercent: total > 0 ? (item.female / total) * 100 : 0,
        malePercent: total > 0 ? (item.male / total) * 100 : 0,
        femaleCount: item.female,
        maleCount: item.male,
        total: total,
      }
    })

    const years = percentageData.map((d) => d.year)
    const femalePercentages = percentageData.map((d) => d.femalePercent)
    const malePercentages = percentageData.map((d) => d.malePercent)

    return {
      color: ['#ec4899', '#3b82f6'],
      title: {
        text: mergedLabels.genderDistributionOverTime,
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
        },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const dataIndex = params[0].dataIndex
          const yearData = percentageData[dataIndex]
          return `
            <div style="padding: 8px;">
              <strong>${yearData.year}</strong><br/>
              <div style="display: flex; align-items: center; margin-top: 4px;">
                <span style="display: inline-block; width: 10px; height: 10px; background: #ec4899; border-radius: 50%; margin-right: 6px;"></span>
                Female: ${formatNumber(yearData.femaleCount)} (${yearData.femalePercent.toFixed(1)}%)
              </div>
              <div style="display: flex; align-items: center; margin-top: 4px;">
                <span style="display: inline-block; width: 10px; height: 10px; background: #3b82f6; border-radius: 50%; margin-right: 6px;"></span>
                Male: ${formatNumber(yearData.maleCount)} (${yearData.malePercent.toFixed(1)}%)
              </div>
              <div style="margin-top: 4px; padding-top: 4px; border-top: 1px solid #e5e7eb;">
                Total: ${formatNumber(yearData.total)}
              </div>
            </div>
          `
        },
      },
      legend: {
        data: [mergedLabels.female, mergedLabels.male],
        bottom: 0,
        itemWidth: 12,
        itemHeight: 12,
        textStyle: {
          fontSize: 14,
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: years,
        axisLabel: {
          fontSize: 12,
        },
      },
      yAxis: {
        type: 'value',
        max: 100,
        min: 0,
        axisLabel: {
          formatter: (value: number) => `${value}%`,
          fontSize: 12,
        },
        splitLine: {
          lineStyle: {
            color: (params: any) => {
              return params.value === 50 ? '#ffb800' : '#e5e7eb'
            },
            width: (params: any) => {
              return params.value === 50 ? 2 : 1
            },
            type: (params: any) => {
              return params.value === 50 ? 'solid' : 'dashed'
            },
          },
        },
      },
      series: [
        {
          name: mergedLabels.female,
          type: 'line',
          stack: 'Total',
          smooth: true,
          lineStyle: { width: 0 },
          showSymbol: false,
          areaStyle: { opacity: 0.8 },
          emphasis: { focus: 'series' },
          data: femalePercentages,
        },
        {
          name: mergedLabels.male,
          type: 'line',
          stack: 'Total',
          smooth: true,
          lineStyle: { width: 0 },
          showSymbol: false,
          areaStyle: { opacity: 0.8 },
          emphasis: { focus: 'series' },
          data: malePercentages,
          markLine: {
            symbol: 'none',
            data: [
              {
                yAxis: 50,
                lineStyle: {
                  color: '#ffb800',
                  width: 2,
                  type: 'solid',
                },
                label: {
                  show: true,
                  position: 'end',
                  formatter: mergedLabels.genderParity,
                  color: '#ffb800',
                  fontSize: 12,
                  fontWeight: 600,
                  backgroundColor: '#fff',
                  padding: [4, 8],
                  borderRadius: 4,
                },
              },
            ],
          },
        },
      ],
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 100,
        },
        {
          start: 0,
          end: 100,
          handleIcon:
            'path://M306.1,413c0,2.2-1.8,4-4,4h-59.8c-2.2,0-4-1.8-4-4V200.8c0-2.2,1.8-4,4-4h59.8c2.2,0,4,1.8,4,4V413z',
          handleSize: '110%',
          handleStyle: {
            color: '#e5e7eb',
          },
          textStyle: {
            color: '#999',
          },
          borderColor: '#e5e7eb',
        },
      ],
    }
  }, [data])

  const participantTypesOption = useMemo(() => {
    if (!data || !data.participantTypesByLegalForm) return null

    return {
      title: {
        text: mergedLabels.participantTypesByLegalForm,
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'horizontal',
        bottom: 0,
        data: data.participantTypesByLegalForm.map((item) => item.type),
      },
      series: [
        {
          name: 'Legal Form',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 20,
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data: data.participantTypesByLegalForm.map((item) => ({
            value: item.count,
            name: item.type,
          })),
        },
      ],
    }
  }, [data])

  if (loading) {
    return (
      <DashboardContainer>
        <Flex justify="center" align="center" style={{ minHeight: '400px' }}>
          <Loading size="large" />
        </Flex>
      </DashboardContainer>
    )
  }

  if (error || !data) {
    return (
      <DashboardContainer>
        <Flex
          justify="center"
          align="center"
          style={{ minHeight: '400px', color: '#999' }}
        >
          {error || mergedLabels.noData}
        </Flex>
      </DashboardContainer>
    )
  }

  const totalBuyersMale = data.buyersGender.male
  const totalBuyersFemale = data.buyersGender.female
  const totalBuyers = totalBuyersMale + totalBuyersFemale
  const buyersMalePercent =
    totalBuyers > 0 ? (totalBuyersMale / totalBuyers) * 100 : 0
  const buyersFemalePercent =
    totalBuyers > 0 ? (totalBuyersFemale / totalBuyers) * 100 : 0

  const totalSellersMale = data.sellersGender.male
  const totalSellersFemale = data.sellersGender.female
  const totalSellers = totalSellersMale + totalSellersFemale
  const sellersMalePercent =
    totalSellers > 0 ? (totalSellersMale / totalSellers) * 100 : 0
  const sellersFemalePercent =
    totalSellers > 0 ? (totalSellersFemale / totalSellers) * 100 : 0

  return (
    <DashboardContainer>
      <MetricsGrid>
        <MetricCard>
          <Icon name="users" size="medium" />
          <MetricValue>{formatNumber(data.buyersCount)}</MetricValue>
          <MetricLabel>{mergedLabels.totalBuyers}</MetricLabel>
        </MetricCard>

        <MetricCard>
          <Icon name="user-check" size="medium" />
          <MetricValue>{formatNumber(data.sellersCount)}</MetricValue>
          <MetricLabel>{mergedLabels.totalSellers}</MetricLabel>
        </MetricCard>

        <MetricCard>
          <Icon name="user" size="medium" />
          <MetricValue>{formatNumber(data.personsCount)}</MetricValue>
          <MetricLabel>{mergedLabels.individualParticipants}</MetricLabel>
        </MetricCard>

        <MetricCard>
          <Icon name="building" size="medium" />
          <MetricValue>{formatNumber(data.companiesCount)}</MetricValue>
          <MetricLabel>{mergedLabels.companyParticipants}</MetricLabel>
        </MetricCard>
      </MetricsGrid>

      <ChartsGrid>
        <SmallChartsGrid>
          <ChartContainer>
            <ChartTitle>{mergedLabels.buyersByGender}</ChartTitle>
            <Flex style={{ borderRadius: '8px', overflow: 'hidden' }}>
              {buyersFemalePercent > 0 && (
                <GenderBar
                  $gender="female"
                  style={{ flex: buyersFemalePercent }}
                >
                  {formatPercentage(buyersFemalePercent)}
                </GenderBar>
              )}
              {buyersMalePercent > 0 && (
                <GenderBar $gender="male" style={{ flex: buyersMalePercent }}>
                  {formatPercentage(buyersMalePercent)}
                </GenderBar>
              )}
            </Flex>
            <GenderLegend>
              <LegendItem>
                <LegendDot $color="#ec4899" />
                {mergedLabels.female}: {formatNumber(totalBuyersFemale)}
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#3b82f6" />
                {mergedLabels.male}: {formatNumber(totalBuyersMale)}
              </LegendItem>
            </GenderLegend>
          </ChartContainer>

          <ChartContainer>
            <ChartTitle>{mergedLabels.sellersByGender}</ChartTitle>
            <Flex style={{ borderRadius: '8px', overflow: 'hidden' }}>
              {sellersFemalePercent > 0 && (
                <GenderBar
                  $gender="female"
                  style={{ flex: sellersFemalePercent }}
                >
                  {formatPercentage(sellersFemalePercent)}
                </GenderBar>
              )}
              {sellersMalePercent > 0 && (
                <GenderBar $gender="male" style={{ flex: sellersMalePercent }}>
                  {formatPercentage(sellersMalePercent)}
                </GenderBar>
              )}
            </Flex>
            <GenderLegend>
              <LegendItem>
                <LegendDot $color="#ec4899" />
                {mergedLabels.female}: {formatNumber(totalSellersFemale)}
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#3b82f6" />
                {mergedLabels.male}: {formatNumber(totalSellersMale)}
              </LegendItem>
            </GenderLegend>
          </ChartContainer>
        </SmallChartsGrid>

        <ChartContainer>
          <ChartTitle>{mergedLabels.personVsCompany}</ChartTitle>
          <Flex
            justify="space-around"
            align="center"
            style={{ padding: '20px 0' }}
          >
            <Flex direction="column" align="center" gap={8}>
              <Icon name="user" size="large" />
              <div style={{ fontSize: '32px', fontWeight: 700 }}>
                {formatPercentage(
                  (data.personsCount /
                    (data.personsCount + data.companiesCount)) *
                    100
                )}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {mergedLabels.individuals}
              </div>
            </Flex>
            <div
              style={{
                width: '2px',
                height: '60px',
                background: '#e5e7eb',
              }}
            />
            <Flex direction="column" align="center" gap={8}>
              <Icon name="building" size="large" />
              <div style={{ fontSize: '32px', fontWeight: 700 }}>
                {formatPercentage(
                  (data.companiesCount /
                    (data.personsCount + data.companiesCount)) *
                    100
                )}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {mergedLabels.companies}
              </div>
            </Flex>
          </Flex>
        </ChartContainer>

        {participantTypesOption && (
          <ChartContainer>
            <ReactECharts
              option={participantTypesOption}
              style={{ height: '400px' }}
            />
          </ChartContainer>
        )}

        {genderOverTimeOption && (
          <ChartContainer style={{ gridColumn: '1 / -1' }}>
            <ReactECharts
              option={genderOverTimeOption}
              style={{ height: '500px' }}
            />
          </ChartContainer>
        )}
      </ChartsGrid>
    </DashboardContainer>
  )
}
